import React, { useEffect, useState } from 'react';
import { Wallet, Pencil, ArrowRightLeft, AlertTriangle, Loader2, Search, Plus, X } from 'lucide-react';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import { avisoPagadorCompartilhado, nomesDePagadorIguais, pagadorComOutroNome, type PagadorUc } from '../../src/lib/pagadorUc';
import { situacaoDocumento } from '../../src/lib/pagadorBoleto';
import { podeEditarCadastro } from '../../src/lib/permissaoCadastro';
import { motivoValido, MOTIVO_MINIMO } from '../../src/lib/alteracoesCadastro';
import {
    atualizarPagador, buscarPagadorDaUc, contarUcsDoPagador, criarEVincularPagador,
    pagadorPorDocumento, procurarPagadores, vincularPagador, type DadosPagador,
} from '../../src/services/pagadores';

// #6562: "Cadastrar pagador novo" com um CPF/CNPJ que já tem pagador de outro nome.
// Antes a UC era ligada ao pagador antigo sem aviso e o nome digitado se perdia.
interface ConflitoDeNome {
    existente: PagadorUc;
    digitado: DadosPagador;
    motivo: string;
    /** Outras UCs (fora esta) que usam o pagador já cadastrado. */
    outrasUcs: number;
}

const textoOutrasUcs = (n: number) => (n === 1 ? 'da outra UC que usa este pagador' : `das outras ${n} UCs que usam este pagador`);

// "Pagador do boleto" no cadastro da UC (migration 20261013180000). Quem paga pode
// ser outra pessoa que não o cliente: nas UCs de Morrinhos o cliente é a
// Prefeitura e quem paga é o Fundo de cada Secretaria. O mesmo pagador serve
// várias UCs — editar aqui muda todas elas.

// #6239: editar ou trocar o pagador é dado cadastral — só Financeiro (permissão
// "Editar dados cadastrais") ou adm. O banco barra do mesmo jeito.
//
// UC em operação: editar ou trocar pede o motivo, que o Faturamento vê no cálculo e
// na emissão do boleto (src/lib/alteracoesCadastro.ts, migration 20261029180000).

const formatarDoc = (d: string | null) => {
    if (!d) return 'sem CPF/CNPJ';
    if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    return d;
};

const inputCls = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/40';

const CampoMotivo: React.FC<{ id: string; valor: string; onChange: (v: string) => void; autoFocus?: boolean }> = ({ id, valor, onChange, autoFocus }) => (
    <div>
        <label htmlFor={id} className="text-xs font-semibold text-gray-600 dark:text-gray-300">Motivo da mudança *</label>
        <textarea
            id={id} rows={2} autoFocus={autoFocus} value={valor} onChange={e => onChange(e.target.value)}
            placeholder="Ex.: pedido do Ítalo (28/10): o filho assumiu o pagamento."
            className={`${inputCls} mt-1`}
        />
        <p className="text-[11px] text-gray-500 dark:text-gray-400">O Faturamento vê o motivo ao calcular e emitir o boleto. Pelo menos {MOTIVO_MINIMO} letras.</p>
    </div>
);

const FormPagador: React.FC<{
    inicial: DadosPagador; salvando: boolean; aviso?: string | null; rotulo: string;
    exigeMotivo?: boolean; motivoInicial?: string;
    onSalvar: (d: DadosPagador, motivo: string) => void; onCancelar: () => void;
}> = ({ inicial, salvando, aviso, rotulo, exigeMotivo, motivoInicial, onSalvar, onCancelar }) => {
    const [f, setF] = useState<DadosPagador>(inicial);
    const [motivo, setMotivo] = useState(motivoInicial ?? '');
    const situ = situacaoDocumento(f.cpf_cnpj);
    const faltaMotivo = !!exigeMotivo && !motivoValido(motivo);
    return (
        <form className="space-y-3" onSubmit={e => { e.preventDefault(); e.stopPropagation(); if (!faltaMotivo) onSalvar(f, motivo.trim()); }}>
            {aviso ? (
                <p className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                    <AlertTriangle size={14} className="shrink-0 mt-0.5" /> {aviso}
                </p>
            ) : null}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Nome *
                    <input className={`${inputCls} mt-1`} value={f.nome} onChange={e => setF({ ...f, nome: e.target.value })} required />
                </label>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">CPF/CNPJ *
                    <input className={`${inputCls} mt-1`} value={f.cpf_cnpj} onChange={e => setF({ ...f, cpf_cnpj: e.target.value })} required inputMode="numeric" />
                    {f.cpf_cnpj && situ === 'invalido' ? <span className="text-[11px] text-red-600 dark:text-red-400">Documento inválido</span> : null}
                </label>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">E-mail (opcional)
                    <input className={`${inputCls} mt-1`} type="email" value={f.email ?? ''} onChange={e => setF({ ...f, email: e.target.value })} />
                </label>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">WhatsApp (opcional)
                    <input className={`${inputCls} mt-1`} value={f.whatsapp ?? ''} onChange={e => setF({ ...f, whatsapp: e.target.value })} />
                </label>
            </div>
            {exigeMotivo ? <CampoMotivo id="motivo-form-pagador" valor={motivo} onChange={setMotivo} /> : null}
            <div className="flex justify-end gap-2">
                <button type="button" onClick={onCancelar} className="px-3 py-1.5 rounded-lg text-xs font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">Cancelar</button>
                <button type="submit" disabled={salvando || situ === 'invalido' || faltaMotivo} className="px-3 py-1.5 rounded-lg text-xs font-bold bg-primary text-white hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-1.5">
                    {salvando ? <Loader2 size={13} className="animate-spin" /> : null} {rotulo}
                </button>
            </div>
        </form>
    );
};

const PagadorUcSecao: React.FC<{
    ucId: number; nomeCliente?: string | null; cpfCliente?: string | null;
    /** UC em operação: editar ou trocar o pagador pede motivo. */
    exigeMotivo?: boolean;
}> = ({ ucId, nomeCliente, cpfCliente, exigeMotivo = false }) => {
    const { user, profile } = useAuth();
    const { addToast } = useToast();
    const podeEditar = podeEditarCadastro(profile);

    const [pagador, setPagador] = useState<PagadorUc | null>(null);
    const [totalUcs, setTotalUcs] = useState(0);
    const [estado, setEstado] = useState<'carregando' | 'pronto' | 'erro'>('carregando');
    const [modo, setModo] = useState<'ver' | 'editar' | 'trocar' | 'novo'>('ver');
    const [salvando, setSalvando] = useState(false);
    const [busca, setBusca] = useState('');
    const [achados, setAchados] = useState<PagadorUc[]>([]);
    const [buscando, setBuscando] = useState(false);
    const [motivoTroca, setMotivoTroca] = useState('');
    // "Usar o nome do cliente" (#6565) abre a edição já com o nome do cliente.
    const [nomeSugerido, setNomeSugerido] = useState<string | null>(null);
    const [conflito, setConflito] = useState<ConflitoDeNome | null>(null);
    // O que foi digitado em "Cadastrar pagador novo", para "Voltar" não apagar.
    const [rascunhoNovo, setRascunhoNovo] = useState<{ dados: DadosPagador; motivo: string } | null>(null);
    const outroNome = pagadorComOutroNome(pagador, nomeCliente, cpfCliente);
    const faltaMotivoTroca = exigeMotivo && !motivoValido(motivoTroca);

    const carregar = async () => {
        setEstado('carregando');
        try {
            const p = await buscarPagadorDaUc(ucId);
            setPagador(p);
            setTotalUcs(p ? await contarUcsDoPagador(p.id) : 0);
            setEstado('pronto');
        } catch {
            setEstado('erro');
        }
    };
    const sairDoNovo = () => { setModo('ver'); setMotivoTroca(''); setConflito(null); setRascunhoNovo(null); };
    useEffect(() => { sairDoNovo(); carregar(); }, [ucId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (modo !== 'trocar' || busca.trim().length < 3) { setAchados([]); return; }
        let vivo = true;
        setBuscando(true);
        const t = setTimeout(() => {
            procurarPagadores(busca)
                .then(r => { if (vivo) setAchados(r.filter(x => x.id !== pagador?.id)); })
                .catch(() => { if (vivo) setAchados([]); })
                .finally(() => { if (vivo) setBuscando(false); });
        }, 300);
        return () => { vivo = false; clearTimeout(t); };
    }, [busca, modo, pagador?.id]);

    const salvarEdicao = async (d: DadosPagador, motivo: string) => {
        if (!pagador) return;
        setSalvando(true);
        try {
            await atualizarPagador(pagador.id, d, user?.id, motivo || null);
            addToast(totalUcs > 1 ? `Pagador atualizado nas ${totalUcs} UCs.` : 'Pagador atualizado.', 'success');
            setModo('ver'); await carregar();
        } catch (e: any) {
            addToast(e?.message || 'Não foi possível salvar o pagador.', 'error', 10000);
        } finally { setSalvando(false); }
    };

    const vincular = async (p: PagadorUc) => {
        if (faltaMotivoTroca) return;
        setSalvando(true);
        try {
            await vincularPagador(ucId, p.id, motivoTroca.trim() || null);
            addToast(`Esta UC passou a ter o pagador ${p.nome}.`, 'success');
            setModo('ver'); setBusca(''); setMotivoTroca(''); await carregar();
        } catch (e: any) {
            addToast(e?.message || 'Não foi possível trocar o pagador.', 'error', 10000);
        } finally { setSalvando(false); }
    };

    const criarNovo = async (d: DadosPagador, motivo: string) => {
        setSalvando(true);
        try {
            // Só lê: se o documento já tem pagador com outro nome, pergunta qual vale.
            const existente = await pagadorPorDocumento(d.cpf_cnpj);
            if (existente && !nomesDePagadorIguais(existente.nome, d.nome)) {
                const usadoEm = await contarUcsDoPagador(existente.id);
                const outrasUcs = Math.max(0, usadoEm - (existente.id === pagador?.id ? 1 : 0));
                setRascunhoNovo({ dados: d, motivo });
                setConflito({ existente, digitado: d, motivo, outrasUcs });
                return;
            }
            const p = await criarEVincularPagador(ucId, d, motivo || null);
            addToast(`Esta UC passou a ter o pagador ${p.nome}.`, 'success');
            sairDoNovo(); await carregar();
        } catch (e: any) {
            addToast(e?.message || 'Não foi possível criar o pagador.', 'error', 10000);
        } finally { setSalvando(false); }
    };

    // Vale o nome digitado: corrige o pagador do documento (todas as UCs dele) e liga esta UC.
    const corrigirNomeEUsar = async () => {
        if (!conflito) return;
        const { existente, digitado, motivo, outrasUcs } = conflito;
        const nome = digitado.nome.trim();
        setSalvando(true);
        try {
            await atualizarPagador(existente.id, {
                nome,
                cpf_cnpj: existente.cpf_cnpj ?? digitado.cpf_cnpj,
                email: digitado.email?.trim() || existente.email,
                whatsapp: digitado.whatsapp?.trim() || existente.whatsapp,
            }, user?.id, motivo || null);
            if (existente.id !== pagador?.id) await vincularPagador(ucId, existente.id, motivo || null);
            addToast(outrasUcs > 0
                ? `Pagador corrigido para ${nome}, nesta UC e no boleto ${textoOutrasUcs(outrasUcs)}.`
                : `Esta UC passou a ter o pagador ${nome}.`, 'success');
            sairDoNovo(); await carregar();
        } catch (e: any) {
            addToast(e?.message || 'Não foi possível corrigir o pagador.', 'error', 10000);
        } finally { setSalvando(false); }
    };

    // Vale o nome já cadastrado: só liga esta UC a ele.
    const manterNomeCadastrado = async () => {
        if (!conflito) return;
        const { existente, motivo } = conflito;
        if (existente.id === pagador?.id) {
            addToast(`Nada mudou: esta UC já usa o pagador ${existente.nome}.`, 'info');
            sairDoNovo();
            return;
        }
        setSalvando(true);
        try {
            await vincularPagador(ucId, existente.id, motivo || null);
            addToast(`Esta UC passou a ter o pagador ${existente.nome}.`, 'success');
            sairDoNovo(); await carregar();
        } catch (e: any) {
            addToast(e?.message || 'Não foi possível trocar o pagador.', 'error', 10000);
        } finally { setSalvando(false); }
    };

    return (
        <section className="mb-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4" aria-labelledby={`pagador-uc-${ucId}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <h3 id={`pagador-uc-${ucId}`} className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Wallet size={16} className="text-primary dark:text-primary-light" /> Pagador do boleto
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Quem aparece no boleto. Pode ser diferente do cliente da UC.</p>
                    {!podeEditar ? (
                        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">Só o Financeiro altera o pagador. Se estiver errado, peça a correção a eles.</p>
                    ) : null}
                </div>
                {podeEditar && estado === 'pronto' && modo === 'ver' ? (
                    <div className="flex gap-2">
                        {pagador ? (
                            <button onClick={() => setModo('editar')} className="px-3 py-1.5 rounded-lg text-xs font-bold border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 inline-flex items-center gap-1.5">
                                <Pencil size={13} /> Editar
                            </button>
                        ) : null}
                        <button onClick={() => setModo('trocar')} className="px-3 py-1.5 rounded-lg text-xs font-bold border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 inline-flex items-center gap-1.5">
                            <ArrowRightLeft size={13} /> {pagador ? 'Trocar pagador' : 'Definir pagador'}
                        </button>
                    </div>
                ) : null}
            </div>

            <div className="mt-3">
                {estado === 'carregando' ? (
                    <div className="flex items-center gap-2 text-xs text-gray-500"><Loader2 size={14} className="animate-spin" /> Carregando pagador…</div>
                ) : estado === 'erro' ? (
                    <p className="text-xs text-red-600 dark:text-red-400">Não foi possível carregar o pagador.</p>
                ) : modo === 'editar' && pagador ? (
                    <FormPagador
                        inicial={{ nome: nomeSugerido ?? pagador.nome, cpf_cnpj: pagador.cpf_cnpj ?? '', email: pagador.email, whatsapp: pagador.whatsapp }}
                        aviso={avisoPagadorCompartilhado(totalUcs)}
                        salvando={salvando} rotulo="Salvar pagador" exigeMotivo={exigeMotivo}
                        onSalvar={(d, m) => { setNomeSugerido(null); salvarEdicao(d, m); }}
                        onCancelar={() => { setNomeSugerido(null); setModo('ver'); }}
                    />
                ) : modo === 'novo' && conflito ? (
                    <div className="space-y-3">
                        <div role="alert" className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                            <div className="min-w-0 space-y-2">
                                <p>O CPF/CNPJ {formatarDoc(conflito.existente.cpf_cnpj)} já tem pagador cadastrado com outro nome. Um CPF/CNPJ sai com um nome só no boleto: qual vale?</p>
                                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                                    <dt>Cadastrado</dt>
                                    <dd className="font-bold break-words">{conflito.existente.nome}</dd>
                                    <dt>Digitado</dt>
                                    <dd className="font-bold break-words">{conflito.digitado.nome.trim()}</dd>
                                </dl>
                                {conflito.outrasUcs > 0 ? (
                                    <p>Corrigir o nome muda também o boleto {textoOutrasUcs(conflito.outrasUcs)}.</p>
                                ) : null}
                            </div>
                        </div>
                        <div className="flex flex-wrap justify-end gap-2">
                            <button type="button" disabled={salvando} onClick={() => setConflito(null)}
                                className="px-3 py-1.5 rounded-lg text-xs font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50">
                                Voltar
                            </button>
                            <button type="button" disabled={salvando} onClick={manterNomeCadastrado}
                                className="px-3 py-1.5 rounded-lg text-xs font-bold border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                                Manter o nome cadastrado
                            </button>
                            <button type="button" disabled={salvando} onClick={corrigirNomeEUsar}
                                className="px-3 py-1.5 rounded-lg text-xs font-bold bg-primary text-white hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-1.5">
                                {salvando ? <Loader2 size={13} className="animate-spin" /> : null} Corrigir o nome e usar nesta UC
                            </button>
                        </div>
                    </div>
                ) : modo === 'novo' ? (
                    <FormPagador
                        inicial={rascunhoNovo?.dados ?? { nome: nomeCliente ?? '', cpf_cnpj: cpfCliente ?? '', email: '', whatsapp: '' }}
                        salvando={salvando} rotulo="Criar e usar nesta UC"
                        exigeMotivo={exigeMotivo} motivoInicial={rascunhoNovo?.motivo ?? motivoTroca}
                        onSalvar={criarNovo} onCancelar={() => { setRascunhoNovo(null); setModo('trocar'); }}
                    />
                ) : modo === 'trocar' ? (
                    <div className="space-y-2">
                        {exigeMotivo ? <CampoMotivo id={`motivo-troca-pagador-${ucId}`} valor={motivoTroca} onChange={setMotivoTroca} autoFocus /> : null}
                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <label htmlFor={`busca-pagador-${ucId}`} className="sr-only">Procurar pagador por nome ou CPF/CNPJ</label>
                            <input id={`busca-pagador-${ucId}`} autoFocus={!exigeMotivo} className={`${inputCls} pl-9`} placeholder="Procurar pagador por nome ou CPF/CNPJ…" value={busca} onChange={e => setBusca(e.target.value)} />
                        </div>
                        {buscando ? <p className="text-xs text-gray-500">Procurando…</p> : null}
                        {achados.length > 0 && faltaMotivoTroca ? (
                            <p className="text-xs text-amber-700 dark:text-amber-300">Escreva o motivo acima para escolher o pagador.</p>
                        ) : null}
                        {achados.map(p => (
                            <button key={p.id} type="button" disabled={salvando || faltaMotivoTroca} onClick={() => vincular(p)}
                                className="w-full text-left rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-50">
                                <span className="block text-sm font-semibold text-gray-900 dark:text-white">{p.nome}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">{formatarDoc(p.cpf_cnpj)}</span>
                            </button>
                        ))}
                        {busca.trim().length >= 3 && !buscando && achados.length === 0 ? (
                            <p className="text-xs text-gray-500 dark:text-gray-400">Nenhum pagador encontrado.</p>
                        ) : null}
                        <div className="flex justify-between gap-2 pt-1">
                            <button type="button" onClick={() => setModo('novo')} className="px-3 py-1.5 rounded-lg text-xs font-bold text-primary dark:text-primary-light border border-primary/30 hover:bg-primary/10 inline-flex items-center gap-1.5">
                                <Plus size={13} /> Cadastrar pagador novo
                            </button>
                            <button type="button" onClick={() => { setModo('ver'); setBusca(''); setMotivoTroca(''); }} className="px-3 py-1.5 rounded-lg text-xs font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 inline-flex items-center gap-1.5">
                                <X size={13} /> Cancelar
                            </button>
                        </div>
                    </div>
                ) : pagador ? (
                    <div className="text-sm">
                        <p className="font-semibold text-gray-900 dark:text-white">{pagador.nome}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-300">
                            {formatarDoc(pagador.cpf_cnpj)}
                            {pagador.email ? <> · {pagador.email}</> : null}
                            {pagador.whatsapp ? <> · {pagador.whatsapp}</> : null}
                        </p>
                        {totalUcs > 1 ? <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Usado em {totalUcs} UCs.</p> : null}
                        {outroNome ? (
                            <div role="alert" className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                                <div>
                                    O pagador tem o CPF/CNPJ do cliente, mas outro nome: o boleto sai como <strong>{pagador.nome}</strong> e o cliente é <strong>{nomeCliente}</strong>.
                                    {podeEditar ? (
                                        <button type="button" onClick={() => { setNomeSugerido(nomeCliente ?? null); setModo('editar'); }}
                                            className="mt-1.5 block font-bold underline hover:no-underline">
                                            Usar o nome do cliente no pagador
                                        </button>
                                    ) : <span className="block mt-1">Peça a correção ao Financeiro.</span>}
                                </div>
                            </div>
                        ) : null}
                        {pagador.conferir_documento ? (
                            <p className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                                Conferir: este CPF/CNPJ aparece com nomes de pessoas diferentes em outras UCs. Corrija o documento ou vincule o pagador certo.
                            </p>
                        ) : null}
                        {!pagador.cpf_cnpj ? (
                            <p className="mt-2 text-xs text-red-600 dark:text-red-400">Sem CPF/CNPJ: o boleto não sai até preencher.</p>
                        ) : null}
                    </div>
                ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-400">Sem pagador definido: o boleto usa o cliente da UC.</p>
                )}
            </div>
        </section>
    );
};

export default PagadorUcSecao;
